/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from '@react-native-firebase/auth';
import { getNativeAuth } from '../services/firebaseConfig';
import { authBridge, restAuthCache, getCurrentBridgeUser } from '../services/authBridge';
import { useThemeColors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import ScreenBackground from '../components/ScreenBackground';
import { mensajeDeError } from '../utils/mensajeDeError';
import { llamarBorradoDeCuenta, FRASE_BORRADO } from '../services/borrarCuenta';
import { preguntarAntesDeAlgoGrave, avisar } from '../utils/preguntar';

// Pantalla intermedia entre "creé cuenta" y "entro al embudo KYC".
// Bloquea al usuario hasta que confirme su correo en la bandeja de entrada.
// Solo aplica para signup con email/password — los providers federados
// (Google/Apple/Phone) ya garantizan identidad y NO pasan por aquí.
//
// Flujo:
//   1) Al montar, muestra el correo objetivo y un botón "Ya verifiqué".
//   2) "Ya verifiqué" → reload(user) → si emailVerified=true, avanza al KYC.
//      Si sigue false, muestra un toast "aún no detectamos la verificación".
//   3) "Reenviar correo" tiene cooldown de 60s para evitar abuso del SMTP
//      de Firebase (que bloquea por rate limit).
//   4) "Salir" cierra la sesión y vuelve a /auth.
//   5) Polling automático cada 5s: chequea silenciosamente si el usuario ya
//      verificó (típico cuando deja el celular un rato mirando el correo).

const RESEND_COOLDOWN_SECONDS = 60;
const POLL_INTERVAL_MS = 5000;

export default function VerifyEmailScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const role = useAppStore((state) => state.userRole);

  const auth = getNativeAuth();
  // El user puede venir del SDK nativo o del fallback REST (authBridge).
  const initialBridge = getCurrentBridgeUser();

  const [email] = useState<string>(initialBridge?.email ?? '');
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [info, setInfo] = useState<string | null>(null);

  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Navegación post-verificación según el rol — misma lógica que
  // auth.tsx#navigateToRoleDashboard cuando KYC sigue pendiente.
  const goToPostVerifyRoute = () => {
    // SCHOOL_DIRECTOR ya no tiene alta propia (perfil escolar anulado el
    // 5-oct-2026): sigue por el camino normal.
    if (role === 'RENT_CAR_OWNER') {
      router.replace('/(tabs)/dashboard');
      return;
    }
    if (!role) {
      router.replace('/role-selection');
      return;
    }
    router.replace('/kyc-welcome');
  };

  // Si el user hace signOut desde otro lado (o pierde sesión), salimos.
  // Solo cuando NO hay user REST tampoco — si el REST sigue cacheado,
  // el bridge mantiene la sesión activa.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user && !restAuthCache.get()) {
        router.replace('/auth');
      }
    });
    return () => unsubscribe();
  }, [auth, router]);

  // Cooldown del botón "Reenviar".
  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
      return;
    }
    if (cooldownIntervalRef.current) return;
    cooldownIntervalRef.current = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    };
  }, [cooldown]);

  // Polling silencioso cada 5s — si el user verifica en otra ventana mientras
  // mira esta pantalla, lo detectamos automáticamente sin que toque el botón.
  // authBridge.reload() sabe si leer del SDK nativo o del lookup REST.
  useEffect(() => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const verified = await authBridge.reload();
        if (verified) {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          goToPostVerifyRoute();
        }
      } catch {
        // Silencio — el botón manual sigue disponible.
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheck = async () => {
    if (!getCurrentBridgeUser()) {
      router.replace('/auth');
      return;
    }
    setChecking(true);
    setInfo(null);
    try {
      const verified = await authBridge.reload();
      if (verified) {
        goToPostVerifyRoute();
      } else {
        setInfo('Aún no detectamos la verificación. Revisa la bandeja de entrada (y la carpeta de spam).');
      }
    } catch (e: any) {
      setInfo(mensajeDeError(e, 'No pudimos verificar el estado. Intenta de nuevo.'));
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (!getCurrentBridgeUser()) {
      router.replace('/auth');
      return;
    }
    if (cooldown > 0 || resending) return;
    setResending(true);
    setInfo(null);
    try {
      await authBridge.sendVerification();
      setInfo('Correo reenviado. Revisa tu bandeja en unos segundos.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (e: any) {
      const code = String(e?.code ?? '');
      if (code.includes('too-many-requests')) {
        setInfo('Pediste el correo varias veces seguidas. Espera unos minutos antes de pedirlo otra vez.');
        setCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setInfo(mensajeDeError(e, 'No pudimos reenviar el correo. Intenta de nuevo en unos minutos.'));
      }
    } finally {
      setResending(false);
    }
  };

  /**
   * ELIMINAR LA CUENTA SIN HABER VERIFICADO EL CORREO.
   *
   * `auth.tsx` crea el usuario de Auth y escribe /users/{uid} con correo,
   * teléfono y rol ANTES de verificar nada, y luego deja a la persona aquí. Y
   * `_layout.tsx` la devuelve a esta pantalla cada vez que entra. Hasta ahora
   * las únicas salidas eran «Ya verifiqué», «Reenviar» y «Cerrar sesión» — y
   * cerrar sesión NO BORRA NADA.
   *
   * O sea: quien se equivocó al escribir su correo, o a quien el correo le
   * cayó en spam, se quedaba con una cuenta viva y sus datos dentro, y sin
   * forma de deshacerla. La guideline 5.1.1(v) exige que el borrado se pueda
   * iniciar y completar dentro de la app, y Apple prueba exactamente este
   * estado intermedio.
   *
   * La sesión sin verificar SÍ lleva `request.auth`, así que la Cloud Function
   * la acepta igual (functions/src/cuenta.ts).
   *
   * Se pide confirmar en dos pasos, como en el perfil: borrar no se deshace.
   */
  const [borrando, setBorrando] = useState(false);
  /**
   * «ME EQUIVOQUE EN EL CORREO» ES OTRA PUERTA, AUNQUE POR DENTRO SEA ESTA.
   *
   * El 15-oct-2026 un chofer se registro con `@gmil.com` (por `gmail.com`) y se
   * quedo aqui para siempre: a un dominio que no existe no llega ningun enlace.
   * De las cuatro salidas de esta pantalla NINGUNA nombraba su problema. La
   * unica que de verdad lo saca se llamaba «Eliminar esta cuenta», en rojo, y
   * quien se equivoco en una letra no toca un boton que parece que le borra
   * algo suyo.
   *
   * Y el consejo que daba el dialogo era peor que ninguno: «cierra sesion y
   * vuelve a registrarte con el correcto» deja la cuenta mala VIVA, con su
   * telefono dentro y ocupando ese correo en Auth para siempre.
   *
   * Mismo borrado, con el nombre y las palabras de SU problema.
   */
  const handleBorrarCuenta = (porErrorDeCorreo = false) => {
    if (borrando) return;
    // ⚠️ Era `Alert.alert`: en web no se podía confirmar el borrado, y borrar
    // la cuenta es un derecho que Google Play exige que funcione.
    void preguntarAntesDeAlgoGrave(
      porErrorDeCorreo ? 'Empezar de nuevo con el correo correcto' : 'Eliminar esta cuenta',
      porErrorDeCorreo
        ? `Vamos a borrar esta cuenta (${email || 'la del correo mal escrito'}) y te llevamos al registro para que lo escribas bien.

` +
          'Hay que borrarla: mientras exista, ese correo queda ocupado en Go Safely y no lo puedes volver a usar. ' +
          'Todavía no has hecho ningún viaje, así que no pierdes nada.'
        : 'Se borrarán tu cuenta y los datos que ya guardamos (tu correo, tu teléfono y tu perfil). No se puede deshacer.',
      'Cancelar',
      porErrorDeCorreo ? 'Sí, corregirlo' : 'Eliminar'
    ).then(async (si: boolean) => {
      if (!si) return;
      setBorrando(true);
      try {
        await llamarBorradoDeCuenta({ confirmacion: FRASE_BORRADO });
        // La sesión se cierra DESPUÉS de que el servidor confirme: si
        // falla, la persona sigue dentro y puede volver a intentarlo.
        try { await authBridge.signOut(); } catch { /* la cuenta ya no está */ }
        // Al REGISTRO si vino por el correo mal escrito: es lo que quiere hacer
        // ahora mismo. Mandarlo a la portada seria perderlo justo ahi.
        router.replace(porErrorDeCorreo ? '/auth' : '/');
      } catch (e: any) {
        setBorrando(false);
        avisar('No pudimos eliminar tu cuenta', mensajeDeError(e, 'Revisa tu conexión e intenta de nuevo.'));
      }
    });
  };

  const handleLogout = async () => {
    try {
      await authBridge.signOut();
    } catch (e) {
      console.warn('[verify-email] signOut falló:', e);
    }
    router.replace('/auth');
  };

  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <View style={[styles.container, { backgroundColor: colors.background + 'E6' }]}>
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={[styles.iconBubble, { backgroundColor: colors.primary + '15' }]}>
              <Ionicons name="mail-unread" size={48} color={colors.primary} />
            </View>

            <Text style={[styles.title, { color: colors.text }]}>Verifica tu correo</Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              Te enviamos un enlace de verificación a:
            </Text>
            <Text style={[styles.email, { color: colors.primary }]} numberOfLines={1}>
              {email || 'tu correo'}
            </Text>

            <View style={[styles.hintBox, { backgroundColor: colors.backgroundElement, borderColor: colors.border }]}>
              <Ionicons name="information-circle" size={18} color={colors.primary} style={{ marginRight: 8 }} />
              <Text style={[styles.hintText, { color: colors.textSecondary }]}>
                Abre el correo desde tu bandeja, toca el enlace y vuelve aquí. Si no llega, revisa la carpeta de spam.
              </Text>
            </View>

            {info && (
              <View style={[styles.infoBox, { backgroundColor: colors.backgroundElement, borderColor: colors.border }]}>
                <Text style={[styles.infoText, { color: colors.text }]}>{info}</Text>
              </View>
            )}

            <TouchableOpacity
              style={[styles.primaryBtn, { backgroundColor: colors.primary }, checking && { opacity: 0.6 }]}
              onPress={handleCheck}
              disabled={checking}
              activeOpacity={0.8}
            >
              {checking ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <>
                  <Ionicons name="checkmark-circle" size={18} color="#FFF" style={{ marginRight: 8 }} />
                  <Text style={styles.primaryBtnText}>Ya verifiqué</Text>
                </>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.secondaryBtn,
                { borderColor: colors.border },
                (cooldown > 0 || resending) && { opacity: 0.5 },
              ]}
              onPress={handleResend}
              disabled={cooldown > 0 || resending}
              activeOpacity={0.8}
            >
              {resending ? (
                <ActivityIndicator color={colors.primary} />
              ) : (
                <Text style={[styles.secondaryBtnText, { color: colors.primary }]}>
                  {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar correo'}
                </Text>
              )}
            </TouchableOpacity>

            {/* LA SALIDA DE QUIEN SE EQUIVOCO: arriba de las otras dos y sin
                color de peligro. Es la que mas falta hace y la que nadie
                encontraba. Por dentro es el mismo borrado. */}
            <TouchableOpacity
              style={styles.linkBtn}
              onPress={() => handleBorrarCuenta(true)}
              disabled={borrando}
              activeOpacity={0.7}
            >
              <Text style={[styles.linkText, { color: colors.primary, fontWeight: '700', opacity: borrando ? 0.5 : 1 }]}>
                Me equivoqué en el correo
              </Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.linkBtn} onPress={handleLogout} activeOpacity={0.7}>
              <Text style={[styles.linkText, { color: colors.textSecondary }]}>
                Usar otra cuenta · <Text style={{ fontWeight: '700' }}>Cerrar sesión</Text>
              </Text>
            </TouchableOpacity>

            {/* La salida que faltaba. Ver la nota de `handleBorrarCuenta`. */}
            <TouchableOpacity
              style={styles.linkBtn}
              onPress={() => handleBorrarCuenta(false)}
              disabled={borrando}
              activeOpacity={0.7}
            >
              <Text style={[styles.linkText, { color: '#C62828', opacity: borrando ? 0.5 : 1 }]}>
                {borrando ? 'Eliminando…' : 'Eliminar esta cuenta'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  container: {
    flex: 1,
    padding: SIZES.padding,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    borderRadius: 24,
    padding: 28,
    borderWidth: 1,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.08,
    shadowRadius: 20,
    elevation: 6,
  },
  iconBubble: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  title: {
    fontSize: 24,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 4,
  },
  email: {
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 18,
    maxWidth: '100%',
  },
  hintBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    width: '100%',
    marginBottom: 14,
  },
  hintText: {
    flex: 1,
    fontSize: 12.5,
    lineHeight: 18,
  },
  infoBox: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    width: '100%',
    marginBottom: 12,
  },
  infoText: {
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 52,
    width: '100%',
    borderRadius: 14,
    marginTop: 4,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
      },
      android: { elevation: 3 },
    }),
  },
  primaryBtnText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  secondaryBtn: {
    height: 50,
    width: '100%',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    marginTop: 12,
    backgroundColor: 'transparent',
  },
  secondaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
  },
  linkBtn: {
    marginTop: 18,
    paddingVertical: 8,
  },
  linkText: {
    fontSize: 13,
  },
});
